import { Button } from "@/components/ui/baseUi/button"
import { useAppForm } from "@/components/ui/forms/hooks/useFormHook"
import { errorToastClassName } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { signUpFieldsSchema, type SignUpFields } from "@/modules/auth/authTypes"
import { useLanguage } from "@/modules/language/hooks/useLanguage"
import { LoggerType } from "@/modules/utils/loggerUtils"
import { getAuthError } from "@modules/auth/authError"
import { AuthContent, AuthForm, AuthHeader, AuthPage, AuthSubmitButton } from "@modules/auth/components/authComponents"
import { LoginBack } from "@modules/auth/components/loginBack"
import { i18n, word } from "@modules/core/utils/i18n"
import { Path } from "@modules/navigation/routes"
import { AuthError as AmplifyAuthError, signUp as signUpAmplify } from "aws-amplify/auth"
import { Link, useNavigate } from "react-router"
import { toast } from "sonner"

const defaultValues: SignUpFields = {
  email: "",
  password: "",
  isTandCChecked: false,
  isCharterChecked: false,
}

export function SignUpPage() {
  const navigate = useNavigate()
  const language = useLanguage()

  const CGUUrl = Path.Eula
  const UseCharterUrl = Path.UseCharter

  const signUp = async () => {
    try {
      await signUpAmplify({
        username: form.state.values.email,
        password: form.state.values.password,
        options: {
          autoSignIn: true,
          userAttributes: {
            locale: i18n.locale,
          },
        },
      })
      void navigate({ pathname: Path.ConfirmEmail }, { state: { email: form.state.values.email } })
    } catch (e) {
      console.error(LoggerType.SignUp, e)
      if (e instanceof AmplifyAuthError) {
        toast.error(getAuthError(e), { classNames: errorToastClassName() })
      } else {
        toast.error(word("global.error.default"), { classNames: errorToastClassName() })
      }
    }
  }

  const form = useAppForm({
    defaultValues,
    validators: {
      onSubmit: signUpFieldsSchema(language),
      onChange: signUpFieldsSchema(language),
    },
    onSubmit: signUp,
  })

  return (
    <AuthPage>
      <AuthForm
        onSubmit={(e) => {
          e.preventDefault()
          void form.handleSubmit()
        }}
      >
        <AuthHeader title={word("auth.signup.form.title")} subtitle={word("auth.signup.form.subtitle")} />

        <AuthContent>
          <div className="mt-5 flex flex-col gap-4">
            <form.AppField name="email">
              {(field) => (
                <field.Field>
                  <field.Content>
                    <field.Input
                      name="email"
                      autoComplete="new-email"
                      type="email"
                      placeholder={word("auth.form.placeholder.email")}
                    />
                    <field.Message />
                  </field.Content>
                </field.Field>
              )}
            </form.AppField>
            <form.AppField name="password">
              {(field) => (
                <field.Field>
                  <field.Content>
                    <field.Password
                      inputProps={{
                        name: "password",
                        autoComplete: "new-password",
                        placeholder: word("auth.signup.form.placeholder.mdp"),
                      }}
                    />
                    <field.Message />
                  </field.Content>
                </field.Field>
              )}
            </form.AppField>

            <Text fontStyle="BS" className="text-textDefault-caption">
              {word("auth.signup.form.tickBoxesMessage")}
            </Text>

            <div className="flex flex-col gap-4 pl-4">
              <div className="flex items-center gap-4">
                <form.AppField name="isTandCChecked">{(field) => <field.Checkbox size="lg" />}</form.AppField>
                <Text fontStyle="LL">{word("auth.signup.form.termsAndConditions")}</Text>

                <Button
                  variant="underline"
                  className="h-auto py-0"
                  nativeButton={false}
                  render={
                    <Link
                      to={CGUUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-purple-600 hover:text-purple-500!"
                    >
                      {word("global.read")}
                    </Link>
                  }
                />
              </div>

              <div className="flex items-center gap-4">
                <form.AppField name="isCharterChecked">{(field) => <field.Checkbox size="lg" />}</form.AppField>

                <Text fontStyle="LL">{word("auth.signup.form.useCharter")}</Text>

                <Button
                  variant="underline"
                  className="h-auto py-0"
                  nativeButton={false}
                  render={
                    <Link
                      to={UseCharterUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-purple-600 hover:text-purple-500!"
                    >
                      {word("global.read")}
                    </Link>
                  }
                />
              </div>
            </div>

            <form.Subscribe
              selector={(state) => [
                state.isValid,
                state.isSubmitting,
                state.values.isTandCChecked,
                state.values.isCharterChecked,
              ]}
            >
              {([isValid, isSubmitting, isTandCChecked, isCharterChecked]) => (
                <AuthSubmitButton
                  loading={isSubmitting}
                  disabled={!isValid || !isTandCChecked || !isCharterChecked}
                  buttonLabel={word("auth.signup.form.button.signup")}
                />
              )}
            </form.Subscribe>
          </div>
          <LoginBack />
        </AuthContent>
      </AuthForm>
    </AuthPage>
  )
}
